import { useChat } from '@ai-sdk/react'
import type { UIMessage } from 'ai'
import { DefaultChatTransport } from 'ai'
import { useAuthStore } from '@/stores/auth-store'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useV2Conversation, v2Keys } from './use-conversations-v2'

interface ConversationMessage {
  id: string
  role: 'USER' | 'ASSISTANT'
  content: string
  createdAt: string
  metadata?: { parts?: UIMessage['parts'] } | null
}

/**
 * Reconstruct a UIMessage from a DB-persisted ConversationMessage.
 * If metadata.parts exists (rich parts with tool-ui data), use those.
 * Otherwise fall back to a plain text part from the content column.
 */
function toUIMessage(msg: ConversationMessage): UIMessage {
  const role = msg.role === 'USER' ? 'user' as const : 'assistant' as const
  const richParts = msg.metadata?.parts

  if (richParts && Array.isArray(richParts) && richParts.length > 0) {
    return {
      id: msg.id,
      role,
      parts: richParts,
    }
  }

  return {
    id: msg.id,
    role,
    parts: [{ type: 'text' as const, text: msg.content }],
  }
}

export function usePolixChat(conversationId?: string) {
  const { tokens, user } = useAuthStore()
  const queryClient = useQueryClient()
  const [currentAgent, setCurrentAgent] = useState<{
    agentName: string
    action?: string
  } | null>(null)

  const { data: conversationDetail } = useV2Conversation(conversationId)

  // Tracks which conversation we last synced and how many messages it had.
  // This prevents re-syncing the same data and ensures post-stream syncs
  // only fire once the DB refetch returns with NEW messages.
  const syncState = useRef<{
    conversationId: string | undefined
    messageCount: number
    pendingSync: boolean
  }>({ conversationId: undefined, messageCount: 0, pendingSync: false })

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: '/api/v2/chat',
        headers: () => ({
          Authorization: `Bearer ${tokens?.accessToken ?? ''}`,
          'X-Tenant-Id': user?.tenantId ?? '',
        }),
        body: () => ({
          conversationId: conversationId ?? undefined,
        }),
      }),
    [tokens?.accessToken, user?.tenantId, conversationId]
  )

  const chat = useChat({
    transport,
    id: conversationId,
    experimental_throttle: 50,
    onData: (data) => {
      if (data && typeof data === 'object' && 'type' in data) {
        const typed = data as { type: string; data?: unknown }
        if (typed.type === 'data-agent' && typed.data) {
          const agentData = typed.data as {
            agentName: string
            action?: string
          }
          setCurrentAgent(agentData)
        }
      }
    },
    onFinish: () => {
      // Flag that we need to re-sync after the DB refetch returns with new messages.
      syncState.current.pendingSync = true

      queryClient.invalidateQueries({ queryKey: v2Keys.conversations() })
      if (conversationId) {
        queryClient.refetchQueries({ queryKey: v2Keys.conversation(conversationId) })
      }
    },
  })

  const isStreaming = chat.status === 'streaming' || chat.status === 'submitted'

  // Sync messages from DB into useChat state.
  // Triggers:
  //   1. Conversation switch: syncState.conversationId doesn't match current
  //   2. Post-stream sync: pendingSync flag + DB has MORE messages than last sync
  //      On post-stream sync we SKIP setMessages — the in-memory state from the
  //      AI SDK stream already contains rich tool-ui parts (cards, previews).
  //      Overwriting with DB data would lose them. We only update the sync counter
  //      so subsequent conversation switches know the DB is up to date.
  useEffect(() => {
    if (!conversationDetail || !conversationId) return
    if (isStreaming) return

    const dbCount = conversationDetail.messages.length
    if (dbCount === 0) return

    const isConversationSwitch = syncState.current.conversationId !== conversationId
    const dbHasNewMessages = dbCount > syncState.current.messageCount
    const isPostStreamSync = syncState.current.pendingSync && dbHasNewMessages

    if (!isConversationSwitch && !isPostStreamSync) return

    // Only overwrite messages on conversation switch (loading a different chat).
    // Post-stream: the AI SDK already holds the authoritative rich messages.
    if (isConversationSwitch) {
      const uiMessages = conversationDetail.messages.map((msg) =>
        toUIMessage(msg as ConversationMessage)
      )
      chat.setMessages(uiMessages)
    }

    syncState.current = {
      conversationId,
      messageCount: dbCount,
      pendingSync: isPostStreamSync ? false : syncState.current.pendingSync,
    }
  }, [conversationDetail, conversationId, isStreaming])

  // Reset tracking when conversation changes
  useEffect(() => {
    if (conversationId !== syncState.current.conversationId) {
      syncState.current = {
        conversationId: undefined,
        messageCount: 0,
        pendingSync: false,
      }
    }
  }, [conversationId])

  return {
    ...chat,
    currentAgent,
    isStreaming,
  }
}
